import { IGtmDataLayer, IGtmDataLayerEntry, IGtmEventData } from '../../../interfaces/gtm-tracking';
import { gtmEventNames } from '../../../../constants/gtm-tracking.constants';
import { TrackingEvent } from '../../../interfaces/tracking.interfaces';
import type { NativeAppService } from '@shared/common/services/native-app/native-app.service';
import { isPlatformBrowser } from '@angular/common';

export class GtmTracking {
  trackingEnabled = false;
  fakeDataLayer = [];

  constructor(
    private readonly nativeAppService: NativeAppService,
    private readonly platformId: any
  ) {
    /**
     * nativeAppService.appInfo returns some info about the native app when the user-app is running
     * within the limehome native app.
     * This info is set on `window.appInfo` to use in GTM. Some custom variables are defined based on this value existing
     */
    if (isPlatformBrowser(this.platformId)) {
      window['appInfo'] = this.nativeAppService.appInfo;
    }

    if (!window['dataLayer']) {
      window['dataLayer'] = [];
    }
  }

  get dataLayer(): IGtmDataLayer {
    return (this.trackingEnabled ? window['dataLayer'] : this.fakeDataLayer) as IGtmDataLayer;
  }

  /**
   * Enables tracking and pushes previous tracked events into dataLayer
   */
  enableTracking() {
    window['dataLayer'].push(...this.fakeDataLayer);
    this.fakeDataLayer = [];
    this.trackingEnabled = true;
  }

  track(dataLayerEntry: IGtmDataLayerEntry | TrackingEvent): void {
    this.dataLayer.push(dataLayerEntry);
  }

  trackEvent(eventName: gtmEventNames, eventData: IGtmEventData, eventCallback?: Function): void {
    this.track({
      event: eventName,
      ...(eventCallback ? { eventCallback } : {}),
      ...eventData
    });

    // Reset all event data entries in data layer (as they persist by default between events)
    this.track(
      Object.keys(eventData).reduce((result: Object, key: string) => {
        if (eventData[key] !== undefined) {
          result[key] = undefined;
        }

        return result;
      }, {})
    );
  }

  /**
   * Tracks set of new generic events to be sent out to GTM via `dataLayer` for marketing
   * third-party tracking vendors as well as for Google Analytics 4.
   */
  trackEventNew(event: TrackingEvent): void {
    // Reset all event data entries in `dataLayer` (as they persist by default between events)
    // before pushing the event. This is done to make sure we maintain a clean `dataLayer`
    // state, as some events could be pushed to `dataLayer` from GTM as well.
    this.dataLayer.push({
      eventDetails: undefined
    });

    this.dataLayer.push({
      ...event
    });
  }
}
